import { Injectable, inject } from '@angular/core';

import { FixtureViewGroupingConfiguration, OfferGroupV2 } from '@cds/betting-offer/grouping/fixture-view';
import { GridViewGroupingConfiguration } from '@cds/betting-offer/grouping/grid-view';
import { EnhancedFixtureViewGroupingConfiguration, EventDetailsTags, EventModel } from '@frontend/sports/betting-offer/feature/model';
import { LoggerFactory } from '@frontend/sports/common/core/feature/logging';
import { ExpiringCache, ExpiringCacheFactory } from '@frontend/sports/common/core/utils/expiring-cache';
import { ApiBaseSettingsProvider } from '@frontend/sports/content-distribution/feature';
import { LocalStoreService } from '@frontend/vanilla/core';
import { difference, isEqual } from 'lodash-es';
import { Observable, catchError, combineLatest, defaultIfEmpty, filter, forkJoin, iif, map, of, switchMap, tap } from 'rxjs';

import { OfferGroupingApi } from './offer-grouping-api.service';

@Injectable({ providedIn: 'root' })
export class OfferGroupingService {
    private api = inject(OfferGroupingApi);
    private storage = inject(LocalStoreService);
    private apiBaseSettings = inject(ApiBaseSettingsProvider);
    private expiringCacheFactory = inject(ExpiringCacheFactory);

    private readonly sportGrouping = 'grouping-sport';
    private readonly gridGrouping = 'grouping-grid';
    private readonly groupingLanguage = 'grouping-lang';

    private readonly cache: ExpiringCache;
    private cacheNull: any = { null: null };
    private readonly lang: string;
    private invalidGridTimestamp: number | null = null;
    private readonly invalidGridTtl: number = 30 * 1000;

    private logger = inject(LoggerFactory).getLogger('OfferGroupingService');

    constructor() {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        this.cache = this.expiringCacheFactory.create(600);
        this.lang = this.apiBaseSettings.getSettings().lang;
        const groupingStorageKeys = this.getGroupingStorageKeys();
        if (this.isValidGroupingLang) {
            this.initCacheFromStorage(groupingStorageKeys);
        } else {
            this.clearStorageGroupingData(groupingStorageKeys);
        }
    }

    getFixtureGrouping(sportId: number, groupingVersion?: string): Observable<EnhancedFixtureViewGroupingConfiguration | undefined> {
        const key = `${this.sportGrouping}-${sportId}-v2`;
        return this.cache.get<EnhancedFixtureViewGroupingConfiguration>(key).pipe(
            switchMap((cacheConfiguration) => {
                if (this.isValidGroupingId(cacheConfiguration, groupingVersion)) {
                    return of(cacheConfiguration);
                }

                return this.getFixtureGroupingFromApi(sportId, key, groupingVersion);
            }),
        );
    }

    getFixtureGroupingForEventModel(eventModel: EventModel): Observable<EnhancedFixtureViewGroupingConfiguration> {
        return this.getFixtureGrouping(eventModel.sport.id, 'any').pipe(
            map((grouping) => {
                if (!grouping) {
                    throw new Error(`Unable to load fixture grouping for the fixture id: ${eventModel.id}`);
                }

                return grouping;
            }),
        );
    }

    getGridGrouping(sportId: number): Observable<GridViewGroupingConfiguration | undefined> {
        const key = this.gridGrouping;

        const source = this.api.getGridGrouping().pipe(
            catchError((error) => {
                const fallback = this.get<GridViewGroupingConfiguration[]>(key);
                this.logger.error(error, `Failed to fetch GridGrouping. Using fallback from storage: ${!!fallback?.length}`);
                return of(fallback);
            }),
            map((resp) => {
                if (resp?.length) {
                    return resp;
                }
                const fallback = this.get<GridViewGroupingConfiguration[]>(key);
                this.logger.warning(`API returned empty GridGrouping. Using fallback from storage: ${!!fallback?.length}`);
                return fallback;
            }),
            tap((resp) => {
                if (resp?.length) {
                    this.set(key, resp);
                }
            }),
        );

        return this.cache.getOrCreate(key, source).pipe(
            switchMap((value: GridViewGroupingConfiguration[] | undefined) => {
                const now = Date.now();

                // Drop invalid cache entry after its TTL is reached.
                // eslint-disable-next-line eqeqeq
                if (this.invalidGridTimestamp !== null && this.invalidGridTimestamp + this.invalidGridTtl < now) {
                    this.cache.remove(key);
                    this.invalidGridTimestamp = null;

                    return this.getGridGrouping(sportId);
                }
                // eslint-disable-next-line eqeqeq
                if (!value?.length && this.invalidGridTimestamp === null) {
                    this.invalidGridTimestamp = now;
                }

                return value?.length ? of(value.find((group) => group.sportId === sportId) ?? undefined) : of(undefined);
            }),
        );
    }

    private getFixtureGroupingFromApi(
        sportId: number,
        key: string,
        groupingVersion?: string,
    ): Observable<EnhancedFixtureViewGroupingConfiguration | undefined> {
        return this.api.getFixtureGrouping(sportId).pipe(
            switchMap((grouping) => {
                if (grouping && this.isValidGroupingId(grouping, groupingVersion)) {
                    return this.cache.create(key, of(this.enhanceGrouping(grouping)));
                }

                return of(grouping ? this.enhanceGrouping(grouping) : grouping);
            }),
            tap((grouping) => {
                if (this.isValidGroupingId(grouping, groupingVersion)) {
                    this.set(key, grouping);
                }
            }),
        );
    }

    private enhanceGrouping(grouping: FixtureViewGroupingConfiguration): EnhancedFixtureViewGroupingConfiguration {
        const apbGroups: OfferGroupV2[] = [];
        const preCreatedGroups: OfferGroupV2[] = [];

        for (const group of Object.values(grouping.marketGroupsV2)) {
            if (group) {
                const offerGroup: OfferGroupV2 = this.createOfferGroup(group, EventDetailsTags.PriceBoost);
                const offerGroupBAB: OfferGroupV2 = this.createOfferGroup(group, EventDetailsTags.PreCreatedBab);

                if (Object.keys(offerGroup.marketGroupItems).length > 0) {
                    apbGroups.push(offerGroup);
                }

                if (Object.keys(offerGroupBAB.marketGroupItems).length > 0) {
                    preCreatedGroups.push(offerGroupBAB);
                }
            }
        }

        return {
            ...grouping,
            preCreatedGroups,
            apbGroups,
        };
    }

    private createOfferGroup(group: OfferGroupV2, tag: string): OfferGroupV2 {
        return {
            id: group.id,
            order: group.order,
            name: group.name,
            tag: group.tag,
            marketGroupItems: Object.fromEntries(Object.entries(group.marketGroupItems).filter(([_, item]) => item?.tags.some((x) => x.key === tag))),
        };
    }

    getFixtureGroupingForSports(sportIds: number[], groupingVersion?: string): Observable<EnhancedFixtureViewGroupingConfiguration[] | undefined> {
        return forkJoin(
            sportIds.map((id) =>
                this.cache.get<EnhancedFixtureViewGroupingConfiguration>(`${this.sportGrouping}-${id}-v2`).pipe(
                    filter((c) => this.isValidGroupingId(c, groupingVersion)),
                    map((c) => c),
                    filter(Boolean),
                ),
            ),
        ).pipe(
            // in case none of them are cached yet, continue with empty
            defaultIfEmpty([]),
            switchMap((validConfigs) => {
                const missingConfigSportIds = difference(
                    sportIds,
                    validConfigs.map((c) => c.sportId),
                );

                return combineLatest([
                    of(validConfigs),
                    iif(() => missingConfigSportIds.length > 0, this.fetchAndCacheFixtureGroupings(missingConfigSportIds, groupingVersion), of([])),
                ]);
            }),
            map(([cachedConfigs, fetchedConfigs]) => [...cachedConfigs, ...fetchedConfigs]),
        );
    }

    private fetchAndCacheFixtureGroupings(sportIds: number[], groupingVersion?: string): Observable<EnhancedFixtureViewGroupingConfiguration[]> {
        return this.api.getFixtureGroupingBatch(sportIds).pipe(
            switchMap((groupings) =>
                forkJoin(
                    groupings?.map((g) => {
                        if (this.isValidGroupingId(g, groupingVersion)) {
                            return this.cache.create(`${this.sportGrouping}-${g.sportId}-v2`, of(this.enhanceGrouping(g)));
                        }

                        return of(g ? this.enhanceGrouping(g) : g);
                    }) ?? [],
                ),
            ),
            tap((fetchGroupings) => {
                fetchGroupings.forEach((fg) => {
                    if (this.isValidGroupingId(fg, groupingVersion)) {
                        this.set(`${this.sportGrouping}-${fg.sportId}-v2`, fg);
                    }
                });
            }),
        );
    }

    private isValidGroupingId(groupingConfiguration: FixtureViewGroupingConfiguration | undefined, groupingVersion?: string): boolean {
        return !!(groupingConfiguration && (groupingVersion === 'any' || groupingVersion === groupingConfiguration.version));
    }

    private set<T>(key: string, value: T | undefined | null): void {
        this.storage.set(key, value ?? this.cacheNull);
    }

    private get<T = any>(key: string): T | undefined {
        const value = this.storage.get(key);

        if (isEqual(value, this.cacheNull)) {
            return undefined;
        }

        return value as T;
    }

    private remove(key: string): void {
        this.storage.remove(key);
    }

    private getGroupingStorageKeys(): string[] {
        const keyPrefixes = [this.gridGrouping, this.sportGrouping];

        return this.storage.keys().filter((key) => keyPrefixes.some((current) => key.startsWith(current)));
    }

    private get isValidGroupingLang(): boolean {
        return this.get(this.groupingLanguage) === this.lang;
    }

    private clearStorageGroupingData(groupingStorageKeys: string[]): void {
        groupingStorageKeys.forEach((key) => {
            this.remove(key);
        });
        this.set(this.groupingLanguage, this.lang);
    }

    private initCacheFromStorage(groupingStorageKeys: string[]): void {
        groupingStorageKeys.forEach((key) => {
            this.cache.getOrCreate(key, of(this.get(key)));
        });
    }
}
